import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FadeUp from '../components/FadeUp';
import SEO from '../components/SEO';

export default function Privacy() {
  const { lang, t } = useLanguage();

  const seoDesc = lang === 'es'
    ? 'Política de privacidad de Anyelo Elite. Cómo recopilamos, usamos y protegemos tu información personal.'
    : 'Anyelo Elite privacy policy. How we collect, use and protect your personal information.';

  return (
    <main data-testid="privacy-page" style={{ backgroundColor: '#030303', minHeight: '100vh' }}>
      <SEO
        title={lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
        description={seoDesc}
        path="/privacy"
      />
      <div className="mx-auto max-w-[800px] px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <FadeUp>
          <h1
            className="text-3xl sm:text-4xl font-bold text-white mb-2"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}
          >
            {t('privacy_title')}
          </h1>
          <p className="text-white/40 text-sm mb-10" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {lang === 'es' ? 'Última actualización: Marzo 2026' : 'Last updated: March 2026'}
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="prose-legal space-y-6 text-white/60 text-sm sm:text-base leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {lang === 'es' ? <PrivacyContentES /> : <PrivacyContentEN />}
          </div>
        </FadeUp>
      </div>
    </main>
  );
}

function PrivacyContentES() {
  return (
    <>
      <p>
        Anyelo Elite ("nosotros", "nuestro") opera la aplicación móvil Anyelo Elite. Esta página le informa sobre nuestras políticas con respecto a la recopilación, uso y divulgación de datos personales cuando utiliza nuestra aplicación.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Información que recopilamos</h2>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Información de la Cuenta</h3>
        <p>Cuando se registra, recopilamos su nombre, dirección de correo electrónico y contraseña (almacenada de forma segura con cifrado bcrypt).</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Datos de Entrenamiento</h3>
        <p>Almacenamos su historial de reservas, progreso de entrenamiento (peso, porcentaje de grasa corporal) y notas de sesión para proporcionar coaching personalizado.</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Mensajes de Chat</h3>
        <p>Los mensajes entre usted y su coach se almacenan para mantener el historial de conversación.</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Datos de Uso</h3>
        <p>Recopilamos datos básicos de uso para mejorar la experiencia de la aplicación.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Cómo usamos sus datos</h2>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Para proporcionar y mantener nuestro servicio de coaching</li>
          <li>Para programar y gestionar sesiones de entrenamiento</li>
          <li>Para rastrear su progreso físico</li>
          <li>Para comunicarnos con usted sobre sesiones</li>
          <li>Para enviar notificaciones push sobre reservas y mensajes</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Compartir datos</h2>
        <p className="mb-3">No vendemos, intercambiamos ni alquilamos sus datos personales a terceros. Sus datos solo se comparten con:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li><strong className="text-white/80">Su coach (Anyelo)</strong> con fines de entrenamiento</li>
          <li><strong className="text-white/80">Jitsi Meet</strong> para videoconsultas (no se requiere cuenta)</li>
          <li><strong className="text-white/80">Stripe</strong> para procesamiento de pagos seguros</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Seguridad de datos</h2>
        <p>Implementamos medidas de seguridad estándar de la industria, incluyendo contraseñas cifradas, autenticación JWT segura y cifrado HTTPS para todos los datos en tránsito.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Retención de datos</h2>
        <p>Sus datos se conservan mientras su cuenta esté activa. Puede solicitar la eliminación de su cuenta y datos asociados contactando a su coach.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Sus derechos</h2>
        <p className="mb-3">Tiene derecho a:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Acceder a sus datos personales</li>
          <li>Corregir datos inexactos</li>
          <li>Solicitar la eliminación de sus datos</li>
          <li>Exportar sus datos</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contacto</h2>
        <p>Para preguntas sobre esta política de privacidad, contáctenos a través de la función de chat de la aplicación o envíenos un email a <a href="mailto:support@anyelo.es" className="underline underline-offset-4" style={{ color: '#C8FF00' }}>support@anyelo.es</a></p>
      </section>
    </>
  );
}

function PrivacyContentEN() {
  return (
    <>
      <p>
        Anyelo Elite ("we", "our", "us") operates the Anyelo Elite mobile application. This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our app.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Information we collect</h2>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Account Information</h3>
        <p>When you register, we collect your name, email address, and password (stored securely with bcrypt hashing).</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Training Data</h3>
        <p>We store your booking history, training progress (weight, body fat percentage), and session notes to provide personalized coaching.</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Chat Messages</h3>
        <p>Messages between you and your coach are stored to maintain conversation history.</p>

        <h3 className="text-base font-semibold text-white/80 mb-2 mt-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Usage Data</h3>
        <p>We collect basic usage data to improve the app experience.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>How we use your data</h2>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>To provide and maintain our coaching service</li>
          <li>To schedule and manage training sessions</li>
          <li>To track your fitness progress</li>
          <li>To communicate with you about sessions</li>
          <li>To send push notifications about bookings and messages</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Data sharing</h2>
        <p className="mb-3">We do not sell, trade, or rent your personal data to third parties. Your data is only shared with:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li><strong className="text-white/80">Your coach (Anyelo)</strong> for training purposes</li>
          <li><strong className="text-white/80">Jitsi Meet</strong> for video consultations (no account required)</li>
          <li><strong className="text-white/80">Stripe</strong> for secure payment processing</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Data security</h2>
        <p>We implement industry-standard security measures including encrypted passwords, secure JWT authentication, and HTTPS encryption for all data in transit.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Data retention</h2>
        <p>Your data is retained as long as your account is active. You may request deletion of your account and associated data by contacting your coach.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Your rights</h2>
        <p className="mb-3">You have the right to:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>Access your personal data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Export your data</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-3 uppercase tracking-wide" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contact</h2>
        <p>For questions about this privacy policy, contact us through the app's chat feature or email us at <a href="mailto:support@anyelo.es" className="underline underline-offset-4" style={{ color: '#C8FF00' }}>support@anyelo.es</a></p>
      </section>
    </>
  );
}
